/**
 * Pure rules over the platform's country registry
 * (`platform_settings.country_registry`, see `lib/countries.ts` for the entry
 * shape and `hooks/useCountryRegistry.ts` for how it is loaded).
 *
 * Both SPAs read the registry through these functions, so "which prefix gets
 * the flag", "which tax-id kinds does a country offer" and "which order do the
 * pickers show" are answered once.
 *
 * No runtime imports on purpose: the tenant's Node test runner
 * (`hooks/countryRegistryRules.test.ts`) strips types but resolves no
 * extension-less paths, and this module is the one under test. Type-only
 * imports are erased, so they are fine.
 */
import type {
    CountryEInvoiceFormat,
    CountryEntry,
    CountryClearance,
    CountryInvoicing,
    CountryInvoicingMode,
    CountryInvoicingRequirement,
    CountryLanguageRule,
    CountryPaymentQr,
    CountryRegion,
    CountryTax,
    CountryTaxIdRole,
    PhonePrefix,
} from './countries';

/** `country_registry.INVOICING_MODES`. Lives here, not in `countries.ts`, so
 *  this module keeps zero runtime imports (see the header). */
export const INVOICING_MODES: readonly CountryInvoicingMode[] = ['blocked', 'generic', 'waiting'];

/** Mirrors `country_registry.PERSON_FIELDS` / `COMPANY_FIELDS` and friends. */
/** `full_name` is the FULL name (nombre y apellidos): two fields of the customer's
 *  file, see `PERSON_FIELD_FILE_KEYS`. `surname` stopped being a separate option on
 *  2026-09-27 — no country ticked it, so no profile asked for the surname. */
export const PERSON_FIELDS: readonly string[] = ['full_name', 'personal_id', 'address_line1', 'postal_code', 'city'];
/** A profile field → the customer-file fields that must all be filled for it. */
export const PERSON_FIELD_FILE_KEYS: Readonly<Record<string, readonly string[]>> = { full_name: ['full_name', 'surname'] };
export const COMPANY_FIELDS: readonly string[] = ['company', 'tax_id', 'address_line1', 'postal_code', 'city'];
/** A company is always named; its tax id is a field of the profile like the
 *  rest (in every default, not in the UK's). */
export const COMPANY_CORE_FIELDS: readonly string[] = ['company'];
export const DEFAULT_PERSON_FIELDS: readonly string[] = ['full_name', 'address_line1', 'postal_code', 'city'];
export const DEFAULT_COMPANY_FIELDS: readonly string[] = ['company', 'tax_id', 'address_line1', 'postal_code', 'city'];
export const MENTION_KEYS = ['reverse_charge', 'export', 'small_business'] as const;
export const MAX_MENTION = 300;
export const INVOICING_NOTICES: readonly string[] = [
    'storage_notification', 'bookkeeping_system', 'language_tolerated', 'b2b_only',
    'nra_eshop', 'isaf', 'einvoice_on_request', 'registry_footer', 'company_details', 'payment_slip',
    'storage_annual_return', 'in_person_receipts', 'register_number', 'recipient_vat_over',
    'recipient_vat_if_any', 'qr_bill', 'register_entry_footer', 'ksef_access', 'ksef_numbering',
];
export const MAX_DEADLINES = 6;
export const MAX_DEADLINE_NOTE = 200;
export const LANGUAGE_RULES: readonly CountryLanguageRule[] = ['any', 'on_request', 'official'];
export const MAX_LANGUAGES = 5;
export const REQUIREMENT_CODES: readonly string[] = [
    'e_invoice', 'e_invoice_platform', 'reporting', 'fiscalisation', 'receipt_system',
    'audit_file', 'storage', 'bookkeeping', 'other',
];
export const REQUIREMENT_SCOPES = ['all', 'b2b', 'b2c', 'b2g'] as const;
export const REQUIREMENT_COVERAGE = ['covered', 'workshop', 'missing'] as const;
export const MAX_REQUIREMENTS = 8;
export const MAX_REQUIREMENT_NOTE = 200;
/** Mirrors `country_registry.E_INVOICE_FORMATS` / `ISSUER_FIELDS`. */
export const E_INVOICE_FORMATS: readonly CountryEInvoiceFormat[] = ['zugferd'];
export const ISSUER_FIELDS: readonly string[] = ['billing_phone', 'billing_email', 'bank_iban', 'company_number'];
/** Mirrors `country_registry.TAX_ID_ROLES` / `PAYMENT_QR_FORMATS` / `CLEARANCE_PLATFORMS` / `MAX_NUMBER_NAME`. */
export const TAX_ID_ROLES: readonly CountryTaxIdRole[] = ['vat', 'national'];
export const PAYMENT_QR_FORMATS: readonly CountryPaymentQr[] = ['swiss_qr_bill'];
export const CLEARANCE_PLATFORMS: readonly CountryClearance[] = ['ksef'];
export const MAX_NUMBER_NAME = 40;
/** The languages the invoicing provider (Space Invoices) PRINTS a PDF in —
 *  its 18 locales. Mirror of `mapping.PROVIDER_LOCALES` in the tenant,
 *  pinned by `test_invoicing_mapping.py`. */
export const PROVIDER_PDF_LANGUAGES: readonly string[] = [
    'es', 'en', 'fr', 'it', 'de', 'pt', 'sl', 'nl', 'pl', 'hr', 'sv', 'fi', 'et', 'bg', 'cs', 'sk', 'nb', 'is',
];

/** Valid requirements (mirrors `country_registry._normalize_requirements`). */
export function coerceRequirements(raw: unknown): CountryInvoicingRequirement[] {
    if (!Array.isArray(raw)) return [];
    const out: CountryInvoicingRequirement[] = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object') continue;
        const r = item as Record<string, unknown>;
        const note = typeof r.note === 'string' ? r.note.trim().slice(0, MAX_REQUIREMENT_NOTE).trim() : '';
        if (!REQUIREMENT_CODES.includes(String(r.code))) continue;
        if (!(REQUIREMENT_SCOPES as readonly string[]).includes(String(r.scope))) continue;
        if (!(REQUIREMENT_COVERAGE as readonly string[]).includes(String(r.coverage))) continue;
        out.push({ code: String(r.code), scope: r.scope as CountryInvoicingRequirement['scope'],
            coverage: r.coverage as CountryInvoicingRequirement['coverage'], note });
    }
    return out.slice(0, MAX_REQUIREMENTS);
}

/** Valid ISO 639-1 codes, lower case, once each (mirrors `_normalize_languages`). */
export function coerceLanguages(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    const out: string[] = [];
    for (const item of raw) {
        const lang = typeof item === 'string' ? item.trim().toLowerCase() : '';
        if (/^[a-z]{2}$/.test(lang) && !out.includes(lang)) out.push(lang);
    }
    return out.slice(0, MAX_LANGUAGES);
}

/** Whether invoices of this profile can go out in a language the law accepts:
 *  `ok`; `tolerated` (another language is valid but the provider does not
 *  print the country's own); `missing` (the law requires a language the
 *  provider does not print); `unknown` (rule not verified). */
export function languageAvailability(inv: Pick<CountryInvoicing, 'languages' | 'language_rule'>): 'ok' | 'tolerated' | 'missing' | 'unknown' {
    const printable = inv.languages.some((l) => PROVIDER_PDF_LANGUAGES.includes(l));
    switch (inv.language_rule) {
        case 'any': return 'ok';
        case 'on_request': return printable || inv.languages.length === 0 ? 'ok' : 'tolerated';
        case 'official': return printable ? 'ok' : 'missing';
        default: return 'unknown';
    }
}
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Valid deadlines, one per date, sorted, at most `MAX_DEADLINES`
 *  (mirrors `country_registry._normalize_deadlines`). */
export function coerceDeadlines(raw: unknown): { date: string; note: string }[] {
    if (!Array.isArray(raw)) return [];
    const byDate = new Map<string, string>();
    for (const item of raw) {
        if (!item || typeof item !== 'object') continue;
        const r = item as Record<string, unknown>;
        const date = typeof r.date === 'string' ? r.date.trim() : '';
        const note = typeof r.note === 'string' ? r.note.trim().slice(0, MAX_DEADLINE_NOTE).trim() : '';
        if (!DATE_RE.test(date) || Number.isNaN(Date.parse(`${date}T00:00:00Z`)) || !note || byDate.has(date)) continue;
        byDate.set(date, note);
    }
    return [...byDate.entries()].sort(([a], [b]) => a.localeCompare(b)).slice(0, MAX_DEADLINES)
        .map(([date, note]) => ({ date, note }));
}

/** What a country without the key, or an unknown country, offers: nothing. */
export const BLOCKED_INVOICING: Readonly<CountryInvoicing> = Object.freeze({
    mode: 'blocked',
    simplified_threshold: null,
    threshold_includes_tax: true,
    person_fields: [...DEFAULT_PERSON_FIELDS],
    company_fields: [...DEFAULT_COMPANY_FIELDS],
    mentions: { reverse_charge: null, export: null, small_business: null },
    notices: [],
    deadlines: [],
    languages: [],
    language_rule: null,
    requirements: [],
    e_invoice_format: null,
    issuer_fields: [],
    small_business_no_tax_id: false,
    small_business_not_tax_subject: false,
    tax_id_role: null,
    tax_id_name: null,
    company_number_name: null,
    payment_qr: null,
    clearance: null,
    retention_years: null,
    notes: null,
});

function fieldList(raw: unknown, allowed: readonly string[], fallback: readonly string[]): string[] {
    if (!Array.isArray(raw)) return [...fallback];
    const wanted = new Set(raw.filter((f): f is string => typeof f === 'string'));
    return allowed.filter((f) => wanted.has(f));
}

/** A local name for the form (`_short_name`): trimmed, or null. */
function shortName(raw: unknown): string | null {
    return typeof raw === 'string' && raw.trim() ? raw.trim().slice(0, MAX_NUMBER_NAME).trim() : null;
}

/** The country pinned at the top of every picker, ahead of the alphabetical
 *  rest — most workshops are Spanish, and it was first before the registry. */
export const PINNED_COUNTRY = 'ES';

function norm(code: string | null | undefined): string {
    return (code ?? '').trim().toUpperCase();
}

/**
 * The invoicing object of a raw entry, always complete. Mirrors
 * `country_registry._normalize_invoicing`: a missing or malformed object is
 * `blocked` — a country nobody opened on purpose offers nothing.
 */
export function coerceInvoicing(raw: unknown): CountryInvoicing {
    if (!raw || typeof raw !== 'object') {
        return { ...BLOCKED_INVOICING, person_fields: [...BLOCKED_INVOICING.person_fields],
            company_fields: [...BLOCKED_INVOICING.company_fields], mentions: { ...BLOCKED_INVOICING.mentions }, notices: [], deadlines: [],
            languages: [], requirements: [], issuer_fields: [] };
    }
    const r = raw as Record<string, unknown>;
    const mode = (INVOICING_MODES as readonly string[]).includes(String(r.mode))
        ? (r.mode as CountryInvoicingMode)
        : 'blocked';
    const threshold = Number(r.simplified_threshold);
    const retention = Number(r.retention_years);
    const m = (r.mentions && typeof r.mentions === 'object' ? r.mentions : {}) as Record<string, unknown>;
    const mention = (k: string) => (typeof m[k] === 'string' && (m[k] as string).trim() ? (m[k] as string).trim().slice(0, MAX_MENTION) : null);
    const company = fieldList(r.company_fields, COMPANY_FIELDS, DEFAULT_COMPANY_FIELDS);
    return {
        mode,
        simplified_threshold: r.simplified_threshold !== null && r.simplified_threshold !== undefined && Number.isFinite(threshold) && threshold >= 0 ? threshold : null,
        threshold_includes_tax: typeof r.threshold_includes_tax === 'boolean' ? r.threshold_includes_tax : true,
        person_fields: fieldList(r.person_fields, PERSON_FIELDS, DEFAULT_PERSON_FIELDS),
        company_fields: COMPANY_FIELDS.filter((f) => COMPANY_CORE_FIELDS.includes(f) || company.includes(f)),
        mentions: { reverse_charge: mention('reverse_charge'), export: mention('export'), small_business: mention('small_business') },
        notices: Array.isArray(r.notices) ? INVOICING_NOTICES.filter((n) => (r.notices as unknown[]).includes(n)) : [],
        deadlines: coerceDeadlines(r.deadlines),
        languages: coerceLanguages(r.languages),
        language_rule: (LANGUAGE_RULES as readonly string[]).includes(String(r.language_rule)) ? (r.language_rule as CountryLanguageRule) : null,
        requirements: coerceRequirements(r.requirements),
        e_invoice_format: (E_INVOICE_FORMATS as readonly string[]).includes(String(r.e_invoice_format))
            ? (r.e_invoice_format as CountryEInvoiceFormat) : null,
        issuer_fields: fieldList(r.issuer_fields, ISSUER_FIELDS, []),
        small_business_no_tax_id: r.small_business_no_tax_id === true,
        small_business_not_tax_subject: r.small_business_not_tax_subject === true,
        tax_id_role: (TAX_ID_ROLES as readonly string[]).includes(String(r.tax_id_role)) ? (r.tax_id_role as CountryTaxIdRole) : null,
        tax_id_name: shortName(r.tax_id_name),
        company_number_name: shortName(r.company_number_name),
        payment_qr: (PAYMENT_QR_FORMATS as readonly string[]).includes(String(r.payment_qr)) ? (r.payment_qr as CountryPaymentQr) : null,
        clearance: (CLEARANCE_PLATFORMS as readonly string[]).includes(String(r.clearance)) ? (r.clearance as CountryClearance) : null,
        retention_years: Number.isInteger(retention) && retention >= 1 ? retention : null,
        notes: typeof r.notes === 'string' && r.notes.trim() ? r.notes.trim() : null,
    };
}

/**
 * Coerce whatever the server sent into well-formed entries. The backend already
 * normalizes what it stores, so this is only a guard against a partial or older
 * response: an entry without a usable code is dropped, codes are upper-cased,
 * duplicates keep the first occurrence, and missing lists become empty. It never
 * throws — a bad payload yields an empty list, never a crashed page.
 */
export function coerceCountries(raw: unknown): CountryEntry[] {
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    const out: CountryEntry[] = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object') continue;
        const e = item as Record<string, unknown>;
        const code = typeof e.code === 'string' ? norm(e.code) : '';
        if (!/^[A-Z]{2}$/.test(code) || seen.has(code)) continue;
        seen.add(code);
        const region: CountryRegion | null = e.region === 'eu' || e.region === 'us' ? e.region : null;
        const taxes: CountryTax[] = Array.isArray(e.taxes)
            ? e.taxes
                .filter((t): t is { name: unknown; rate: unknown } => !!t && typeof t === 'object')
                .map((t) => ({ name: String(t.name ?? ''), rate: Number(t.rate) }))
                .filter((t) => t.name !== '' && Number.isFinite(t.rate))
            : [];
        out.push({
            code,
            signup: e.signup === true,
            selectable: e.selectable !== false,
            region,
            phone_prefix: typeof e.phone_prefix === 'string' && e.phone_prefix ? e.phone_prefix : null,
            currency: typeof e.currency === 'string' && /^[A-Z]{3}$/.test(e.currency.trim().toUpperCase()) ? e.currency.trim().toUpperCase() : null,
            taxes,
            invoicing: coerceInvoicing(e.invoicing),
            // A BCP-47 tag (`de-AT`, `sr-Latn-RS`); the server checks it is a known one.
            format_locale: typeof e.format_locale === 'string' && /^[a-z]{2}(-[A-Z][a-z]{3})?-[A-Z]{2}$/.test(e.format_locale)
                ? e.format_locale : null,
            // Only the tenant API sends it (derived, not a registry setting).
            ...(typeof e.vies === 'boolean' ? { vies: e.vies } : {}),
        });
    }
    return out;
}

/** The entry for a code (case-insensitive), or `undefined`. */
export function findCountry(
    countries: readonly CountryEntry[],
    code: string | null | undefined,
): CountryEntry | undefined {
    const c = norm(code);
    if (!c) return undefined;
    return countries.find((e) => e.code === c);
}

/** The countries offered in pickers, in registry order. */
export function selectableCountries(countries: readonly CountryEntry[]): CountryEntry[] {
    return countries.filter((e) => e.selectable);
}

/**
 * One phone-prefix option per dial-in code, in registry order. When several
 * countries share a code (``+1`` US/CA/DO, ``+44`` GB/JE/GG/IM, ``+39`` IT/VA)
 * the FIRST of them in the list provides the flag and the others are folded in.
 *
 * Every entry with a prefix counts, selectable or not: this list also splits
 * stored numbers, and a number saved for a country that later stopped being
 * selectable must still come apart at the right place.
 */
export function phonePrefixesFrom(countries: readonly CountryEntry[]): PhonePrefix[] {
    const seen = new Set<string>();
    const out: PhonePrefix[] = [];
    for (const e of countries) {
        const code = e.phone_prefix;
        if (!code || seen.has(code)) continue;
        seen.add(code);
        out.push({ cc: e.code, code });
    }
    return out;
}

/** Taxes a workshop in this country can choose from (first = proposed default).
 *  Unknown country ⇒ `[]`. */
export function taxesFor(
    countries: readonly CountryEntry[],
    code: string | null | undefined,
): CountryTax[] {
    return findCountry(countries, code)?.taxes ?? [];
}

/** The invoicing rules of a country; an unknown country is `blocked`. */
export function invoicingFor(
    countries: readonly CountryEntry[],
    code: string | null | undefined,
): CountryInvoicing {
    return findCountry(countries, code)?.invoicing ?? { ...BLOCKED_INVOICING };
}

export interface CountryOption {
    value: string;
    label: string;
}

/**
 * Picker options for the SELECTABLE countries: `PINNED_COUNTRY` first (when
 * present), the rest sorted by label in `locale`. `displayName` builds the label
 * — pass `countryDisplayName` (the tenant shows the bare name) or a wrapper that
 * appends the code (the admin shows ``España (ES)``). Escape options such as
 * "Other" are the caller's to append.
 */
export function sortedCountryOptions(
    countries: readonly CountryEntry[],
    locale: string,
    displayName: (code: string, locale: string) => string,
): CountryOption[] {
    const opts = selectableCountries(countries).map((e) => ({ value: e.code, label: displayName(e.code, locale) }));
    const head = opts.filter((o) => o.value === PINNED_COUNTRY);
    const tail = opts
        .filter((o) => o.value !== PINNED_COUNTRY)
        .sort((a, b) => a.label.localeCompare(b.label, locale));
    return [...head, ...tail];
}

/** Whether a stored country code is one the pickers offer. */
export function isSelectableCountry(
    countries: readonly CountryEntry[],
    code: string | null | undefined,
): boolean {
    return findCountry(countries, code)?.selectable === true;
}

/** The prefix a blank phone field starts on: the first one in registry order
 *  (Spain's, in the seed), or `''` while the registry is not loaded. */
export function defaultPhonePrefix(prefixes: readonly PhonePrefix[]): string {
    return prefixes[0]?.code ?? '';
}

/** E.164 caps a full international number at 15 digits. */
export const E164_MAX_DIGITS = 15;

/**
 * The longest NATIONAL number a phone field accepts, prefix aside. E.164 caps
 * the whole number at 15 digits and the shortest country code is one digit,
 * so 14 is the ceiling no real number exceeds. Spain's 9 is NOT a rule: German
 * and Austrian numbers reach 11–13, British and Italian ones 10. The customer
 * form used to cut at 9 (and demand exactly 9), so none of those fitted.
 */
export const NATIONAL_PHONE_MAX_DIGITS = 14;
/** Below this it is not a phone number anywhere. */
export const NATIONAL_PHONE_MIN_DIGITS = 4;

/**
 * Split a stored phone (``+34600000000``) into prefix + national digits, using
 * the LONGEST registered prefix that fits — so ``+3906…`` resolves to ``+39``
 * even if a shorter code also matched, and a future longer code wins over a
 * shorter one.
 *
 * - Empty value ⇒ default prefix, no digits.
 * - No ``+`` and no known prefix ⇒ default prefix + the value's digits (the
 *   stored number had no country code; this is how the forms always read it).
 * - A ``+`` number whose code the registry does not list (a country left out,
 *   such as Belarus, or one whose prefix an admin cleared) ⇒
 *   `{ prefix: '', national: stored }`, verbatim. Reading it under the default
 *   prefix turned ``+375…`` into ``+34`` + ``375…``, and the next save of the
 *   form wrote that over the real number.
 * - Registry NOT loaded (`prefixes` empty) ⇒ the same verbatim split, for the
 *   same reason (``+351…`` used to read as ``+34`` + ``351…``).
 *
 * In the verbatim cases `composePhone(prefix, national)` gives back exactly
 * what was stored.
 */
export function splitPhone(
    stored: string | null | undefined,
    prefixes: readonly PhonePrefix[],
): { prefix: string; national: string } {
    const fallback = defaultPhonePrefix(prefixes);
    if (!stored) return { prefix: fallback, national: '' };
    if (prefixes.length === 0) return { prefix: '', national: stored };
    const found = [...prefixes]
        .sort((a, b) => b.code.length - a.code.length)
        .find((p) => stored.startsWith(p.code));
    if (found) return { prefix: found.code, national: stored.substring(found.code.length) };
    if (stored.trimStart().startsWith('+')) return { prefix: '', national: stored };
    return { prefix: fallback, national: stored.replace(/\D/g, '') };
}

/** The stored form of a phone field: prefix + national, or `''` when there is
 *  no number. With an empty prefix the national part already carries its own
 *  ``+`` code (see `splitPhone`). */
export function composePhone(prefix: string, national: string): string {
    return national ? `${prefix}${national}` : '';
}

/**
 * The field's state after the user types `raw` into the national-number input.
 *
 * - With a prefix picked: digits only, capped at `maxLength`.
 * - With NO prefix (the input holds a whole ``+…`` number, see `splitPhone`):
 *   the leading ``+`` is kept, so editing the number never saves it without a
 *   country code. If the user deletes the ``+`` (or empties the field), the
 *   digits become a national number under the default prefix.
 * - With no prefix and no registry there is nothing to put in front; the value
 *   is kept as typed. Callers disable the input in that state
 *   (`prefixes.length === 0`), so this is only a guard.
 */
export function typeNationalNumber(
    raw: string,
    prefix: string,
    prefixes: readonly PhonePrefix[],
    maxLength: number,
): { prefix: string; national: string } {
    const digits = raw.replace(/\D/g, '');
    if (prefix) return { prefix, national: digits.slice(0, maxLength) };
    if (digits && raw.trimStart().startsWith('+')) {
        return { prefix: '', national: `+${digits.slice(0, E164_MAX_DIGITS)}` };
    }
    const fallback = defaultPhonePrefix(prefixes);
    if (!fallback) return { prefix: '', national: raw };
    return { prefix: fallback, national: digits.slice(0, maxLength) };
}

/**
 * The field's state after the user picks `next` in the prefix picker. A
 * national part that still carries its own ``+`` code (an unlisted prefix)
 * loses the ``+`` — otherwise the two codes would be glued together
 * (``+34+375…``). Its digits stay visible for the user to fix.
 */
export function choosePhonePrefix(next: string, national: string): { prefix: string; national: string } {
    return {
        prefix: next,
        national: next && national.trimStart().startsWith('+') ? national.replace(/\D/g, '') : national,
    };
}
